import { DevicePairingDialog } from "@/components/auth/device-pairing-dialog";
import { useState, useEffect, useCallback } from "react";
import { Link, useMatchRoute, useLocation } from "@tanstack/react-router";
import { Menu, Sun, Moon, Search, ChevronRight, LogOut, Smartphone, Settings as SettingsIcon } from "lucide-react";
import { useTheme } from "next-themes";
import { StoreSwitcher } from "./store-switcher";
import { NotificationBell } from "./notification-bell";
import { useSession, useSignOut } from "@/hooks/use-auth";
import { hasRole } from "@/lib/roles";
import { useActiveStore } from "@/hooks/use-active-store";
import { useActiveStoreType } from "@/hooks/use-active-store-type";
import { useNewReturnsCount } from "@/hooks/use-returns";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { VisuallyHidden } from "radix-ui";
import { cn } from "@/lib/utils";
import { KomoranLogo, KomoranIcon } from "./komoran-logo";
import { navGroups, footerItems, visibleItems, type NavItem, type NavGroup, type NavCtx } from "./nav-config";
import { rememberRoute, openCommandPalette } from "./nav-state";
import { useStoreTypeRedirect } from "./store-type-redirect";

const COLLAPSED_KEY = "komoran-collapsed-nav-groups";

function readCollapsed(): string[] {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((g) => typeof g === "string") : [];
  } catch {
    return [];
  }
}

function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <Button
      variant="ghost"
      size="icon-xs"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      title={isDark ? "Svijetla tema" : "Tamna tema"}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
}

function NavLink({
  item,
  isActive,
  badgeCount,
  onNavigate,
}: {
  item: NavItem;
  isActive: boolean;
  badgeCount?: number;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={item.to as any}
      onClick={() => {
        rememberRoute(item.to);
        onNavigate?.();
      }}
      className={cn(
        // A 2px leading bar carries the active state; the background alone was too faint to scan.
        "group relative flex items-center gap-2.5 border-l-2 py-2 pl-3 pr-2.5 text-[13px] transition-colors",
        isActive
          ? "border-primary bg-sidebar-accent font-medium text-sidebar-accent-foreground"
          : "border-transparent text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
      )}
    >
      <item.icon className={cn("h-4 w-4 shrink-0", isActive ? "text-primary" : "text-current")} />
      <span className="truncate">{item.label}</span>
      {!!badgeCount && (
        <span className="ml-auto flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
          {badgeCount}
        </span>
      )}
    </Link>
  );
}

function NavGroupSection({
  group,
  navCtx,
  collapsed,
  onToggle,
  badgeCounts,
  onNavigate,
}: {
  group: NavGroup;
  navCtx: NavCtx;
  collapsed: boolean;
  onToggle: () => void;
  badgeCounts: Record<string, number | undefined>;
  onNavigate?: () => void;
}) {
  const matchRoute = useMatchRoute();
  const items = visibleItems(group.items, navCtx);
  if (items.length === 0) return null;

  const activeItem = items.find((item) => !!matchRoute({ to: item.to as any, fuzzy: true }));

  // Unlabeled group: a flat block at the top, no heading and nothing to collapse.
  if (!group.label) {
    return (
      <div className="flex flex-col pt-2">
        {items.map((item) => (
          <NavLink
            key={item.to}
            item={item}
            isActive={item === activeItem}
            badgeCount={item.badge ? badgeCounts[item.badge] : undefined}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    );
  }

  // A collapsed group still opens itself when it holds the current page, so you are
  // never looking at a closed group with no idea where you are.
  const isOpen = !collapsed || !!activeItem;
  const hiddenBadgeTotal = items
    .filter((item) => item.badge && item !== activeItem)
    .reduce((sum, item) => sum + (badgeCounts[item.badge!] ?? 0), 0);

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-1 px-2.5 pb-1 pt-4 text-[10px] font-medium uppercase tracking-wider text-sidebar-foreground/40 transition-colors hover:text-sidebar-foreground/70"
      >
        <ChevronRight className={cn("h-3 w-3 transition-transform", isOpen && "rotate-90")} />
        {group.label}
        {!isOpen && hiddenBadgeTotal > 0 && (
          <span className="ml-auto flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[9px] text-primary-foreground">
            {hiddenBadgeTotal}
          </span>
        )}
      </button>
      {isOpen && (
        <div className="flex flex-col">
          {items.map((item) => (
            <NavLink
              key={item.to}
              item={item}
              isActive={item === activeItem}
              badgeCount={item.badge ? badgeCounts[item.badge] : undefined}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useSession();
  const signOut = useSignOut();
  const [pairingOpen, setPairingOpen] = useState(false);
  const matchRoute = useMatchRoute();
  const isAdmin = hasRole(user, "admin");
  const { storeId } = useActiveStore();
  const storeType = useActiveStoreType();
  // Dok tip trgovine nije poznat, navigacija se ne crta (ni maloprodajna ni veleprodajna).
  const navCtx: NavCtx | null = storeType ? { isAdmin, isDev: hasRole(user, "dev"), storeType } : null;
  const { data: newReturnsCount } = useNewReturnsCount(storeId);
  const [collapsed, setCollapsed] = useState<string[]>(readCollapsed);

  const toggleGroup = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id];
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next));
      } catch {
        // private mode / quota — collapse state is a nicety, never a hard failure
      }
      return next;
    });
  }, []);

  const badgeCounts = { newReturns: newReturnsCount };
  const visibleFooter = navCtx ? visibleItems(footerItems, navCtx) : [];
  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();

  return (
    <>
      <div className="border-b p-3">
        <div className="mb-2 flex items-center justify-between">
          <KomoranLogo />
          <div className="flex items-center gap-0.5">
            <NotificationBell />
            <ThemeToggle />
          </div>
        </div>
        <StoreSwitcher />
        <button
          type="button"
          onClick={openCommandPalette}
          className="mt-2 flex w-full items-center gap-2 rounded-md border bg-background/50 px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <Search className="h-3.5 w-3.5" />
          Traži…
          <kbd className="ml-auto font-sans text-[10px] tracking-wider opacity-60">⌘K</kbd>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto p-1.5 pt-0">
        {!navCtx && (
          <div className="flex flex-col gap-1.5 px-1 pt-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-7 animate-pulse rounded-md bg-sidebar-accent/40" />
            ))}
          </div>
        )}
        {navCtx && navGroups.map((group) => (
          <NavGroupSection
            key={group.id}
            group={group}
            navCtx={navCtx}
            collapsed={collapsed.includes(group.id)}
            onToggle={() => toggleGroup(group.id)}
            badgeCounts={badgeCounts}
            onNavigate={onNavigate}
          />
        ))}
      </nav>

      <div className="border-t p-1.5">
        {visibleFooter
          .filter((item) => item.to === "/god-view" || item.to === "/bugovi")
          .map((item) => (
            <NavLink
              key={item.to}
              item={item}
              isActive={!!matchRoute({ to: item.to as any, fuzzy: true })}
              onNavigate={onNavigate}
            />
          ))}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-sidebar-accent/50"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-medium text-primary">
                {initials}
              </span>
              <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{user?.email}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-48">
            <DropdownMenuItem asChild>
              <Link
                to="/settings"
                onClick={() => {
                  rememberRoute("/settings");
                  onNavigate?.();
                }}
              >
                <SettingsIcon className="h-4 w-4" />
                Postavke
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setPairingOpen(true)}>
              <Smartphone className="h-4 w-4" />
              Upari Android uređaj
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()}>
              <LogOut className="h-4 w-4" />
              Odjava
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DevicePairingDialog open={pairingOpen} onOpenChange={setPairingOpen} />
      </div>
    </>
  );
}

export function Sidebar() {
  // Jedino mjesto koje je uvijek montirano u aplikaciji — tu se odlučuje o
  // preusmjerenju kad tip aktivne trgovine ne odgovara trenutnoj stranici.
  useStoreTypeRedirect();
  return (
    <aside className="hidden h-screen w-56 flex-col border-r bg-sidebar text-sidebar-foreground lg:flex">
      <SidebarContent />
    </aside>
  );
}

export function MobileHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex items-center gap-2 border-b bg-sidebar px-3 py-2 text-sidebar-foreground lg:hidden">
      <Button variant="ghost" size="icon-xs" onClick={() => setOpen(true)}>
        <Menu className="h-4 w-4" />
      </Button>
      <KomoranIcon className="h-4 w-4" />
      <span className="text-xs font-medium">Komoran</span>
      <div className="ml-auto">
        <NotificationBell align="end" />
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="left"
          showCloseButton={false}
          className="w-56 gap-0 bg-sidebar p-0 text-sidebar-foreground"
        >
          <VisuallyHidden.Root>
            <SheetTitle>Navigacija</SheetTitle>
          </VisuallyHidden.Root>
          <div className="flex h-full flex-col">
            <SidebarContent onNavigate={() => setOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
